import { useState, type FormEvent } from "react"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import { getDocumentTypes } from "@alascorporation/utils"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import { inputCls, labelCls } from "../../components/admin/FormComponents"
import { StudentProfileEditor } from "../../components/admin/StudentProfileEditor"
import { type StudentProfileFields, type SectionId } from "../../components/admin/StudentProfileSections"

function hasAnyProfileData(profile: StudentProfileFields): boolean {
  return Object.values(profile).some((value) => value !== null && value !== undefined && value !== "")
}

export default function StudentCreatePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [profile, setProfile] = useState<StudentProfileFields>({})
  const [activeSection, setActiveSection] = useState<SectionId>("personal")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  function set<K extends keyof StudentProfileFields>(key: K, value: StudentProfileFields[K]) {
    setProfile((prev) => ({ ...prev, [key]: value }))
  }

  function goBack() {
    navigate("/instructor/students")
  }

  const firstName = (profile.firstName ?? "").trim()
  const lastName = (profile.lastName ?? "").trim()
  const fullName = [firstName, lastName].filter(Boolean).join(" ")
  const canSubmit = Boolean(firstName && lastName && email.trim() && !saving)
  const documentTypes = getDocumentTypes(profile.countryCode)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError("")
    try {
      const created = await api.post("/admin/users", { name: fullName, email: email.trim() })
      const newUserId = created.data.data.id as string

      const trimmedPhone = phone.trim()
      if (hasAnyProfileData(profile) || trimmedPhone) {
        await api.patch(`/admin/users/${newUserId}/profile`, {
          ...(trimmedPhone ? { phone: trimmedPhone } : {}),
          profile,
        })
      }

      qc.invalidateQueries({ queryKey: ["server-table", "/admin/users"] })
      toastSuccess(`${fullName} quedó creado — completa el resto de su perfil cuando quieras.`)
      navigate(`/instructor/students/${newUserId}/perfil`)
    } catch (err) {
      const message = getErrorMessage(err)
      setError(message)
      toastError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <StudentProfileEditor
      breadcrumbCurrent="Crear estudiante"
      heroTitle={fullName || "Nuevo estudiante"}
      heroSubtitleItems={[email || "Sin email aún"]}
      profile={profile}
      set={set}
      documentTypes={documentTypes}
      activeSection={activeSection}
      onActiveSectionChange={setActiveSection}
      personalExtra={
        <>
          <div>
            <label className={labelCls}>Email *</label>
            <input
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Número de celular</label>
            <input
              type="tel"
              className={inputCls}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Ej: 3001234567"
            />
          </div>
        </>
      }
      infoMessage="Diligencia nombres, apellidos y email; el resto del perfil se puede completar por partes, igual que al editar un estudiante existente."
      error={error || undefined}
      onSubmit={handleSubmit}
      submitLabel={saving ? "Creando..." : "Crear estudiante"}
      submitDisabled={!canSubmit}
      onCancel={goBack}
      cancelLabel="Cancelar"
      onBackToList={goBack}
    />
  )
}
